import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import axios from "axios";
import Select from "react-select";
import { useForm, useWatch, Controller } from "react-hook-form";
import { useParams } from "react-router-dom"; // Assuming routing with react-router-dom
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSave,
  faCheck,
  faCheckSquare,
  faSquare,
  faExclamationTriangle,
  faBuilding,
  faUser,
  faChild,
  faCalendarAlt,
  faStethoscope,
  faShieldAlt,
  faClock,
  faVenusMars,
  faFileMedical,
  faExclamationCircle,
  faBan,
  faSearch,
  faTimes,
  faClockRotateLeft, // For expired/used
} from "@fortawesome/free-solid-svg-icons";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Swal from "sweetalert2";
import {
  useTable,
  usePagination,
  useGlobalFilter,
  useFilters,
} from "react-table";
import { BASE_URL } from "../../config"; // Assume

const APE_DATEPICKER_PORTAL_ID = "ape-scheduler-datepicker-portal";
const APE_DATEPICKER_POPPER_CLASS = "ape-scheduler-datepicker-popper";
const APE_DATEPICKER_PORTAL_STYLE = `
  #${APE_DATEPICKER_PORTAL_ID} .${APE_DATEPICKER_POPPER_CLASS} {
    z-index: 10000 !important;
  }
`;

const getResponseArray = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.data?.data)) return payload.data.data;
  return [];
};

const normalizePagination = (pagination, data = [], page = 1, perPage = 10) => {
  const currentPage = Number(pagination?.current_page ?? page) || 1;
  const perPageValue = Number(pagination?.per_page ?? perPage) || perPage;
  const total = Number(pagination?.total ?? data.length) || 0;
  const lastPage =
    Number(pagination?.last_page) || Math.max(1, Math.ceil(total / perPageValue));

  return {
    ...(pagination || {}),
    from:
      pagination?.from ??
      (total > 0 ? (currentPage - 1) * perPageValue + 1 : 0),
    to:
      pagination?.to ??
      (total > 0 ? Math.min(currentPage * perPageValue, total) : 0),
    total,
    current_page: currentPage,
    last_page: lastPage,
    per_page: perPageValue,
  };
};

const ApeSchedulerEditPage = () => {
  const { setupId } = useParams(); // Get setup ID from URL, e.g., /edit/:setupId
  const [selectedCorp, setSelectedCorp] = useState(null);
  const [tableData, setTableData] = useState({
    data: [],
    pagination: normalizePagination(),
  });
  const [loading, setLoading] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());
  const [selectAll, setSelectAll] = useState(false);
  const [draftMembers, setDraftMembers] = useState([]); // Loaded draft members for editing
  const [dropdownsLoaded, setDropdownsLoaded] = useState(false); // Track if global dropdowns are loaded
  const [providersLoaded, setProvidersLoaded] = useState(false); // Specifically track providers load
  const draftValuesRef = useRef([]); // Ref to avoid dep issues in callbacks
  const {
    handleSubmit,
    reset,
    control,
    setValue,
    register,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      corporate: null,
      plan: null,
      memberType: "All",
      gender: "All",
      minAge: 1,
      maxAge: 120,
      scheduleType: "S1", // Default
      package: null,
      provider: null,
      remarks: "",
      amount: 0,
      members: [], // Array of selected member objects
    },
    mode: "onChange", // For real-time validation
  });
  const corporate = useWatch({ control, name: "corporate" });
  const plan = useWatch({ control, name: "plan" });
  const memberType = useWatch({ control, name: "memberType" });
  const gender = useWatch({ control, name: "gender" });
  const minAge = useWatch({ control, name: "minAge" });
  const maxAge = useWatch({ control, name: "maxAge" });
  const provider = useWatch({ control, name: "provider" });
  const selectedMembers = watch("members"); // For select all logic
  const [corporates, setCorporates] = useState([]);
  const [plans, setPlans] = useState([{ value: "All", label: "All" }]);
  const [packages, setPackages] = useState([]);
  const [providers, setProviders] = useState([]); // single selection lang ang Providers
  const [membersList, setMembersList] = useState([]); // Raw list for checkboxes (will include all, merged with drafts)
  const [memberSearch, setMemberSearch] = useState("");
  const [tableSearch, setTableSearch] = useState("");
  const [setupData, setSetupData] = useState(null); // Loaded setup details
  const plansLoadedKeyRef = useRef(null);

  const getAuthHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem("authentication/token")}`,
  });

  const getMemberTypeLabel = (value) => {
    const normalized = String(value || "").trim().toLowerCase();

    if (!normalized || normalized === "all" || normalized === "a") return "All";
    if (normalized === "principal" || normalized === "p") return "Principal";
    if (normalized === "dependent" || normalized === "d") return "Dependent";

    return "All";
  };

  const formatCell = (value) => {
    if (value === null || value === undefined || value === "") return "N/A";
    return value;
  };

  const formatDateOnly = (value) => {
    if (!value) return "N/A";
    return String(value).split("T")[0];
  };

  const formatAmount = (value) => {
    if (value === null || value === undefined || value === "") return "N/A";
    const numericValue = Number(value);

    if (Number.isNaN(numericValue)) return value;

    return numericValue.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  // Load Global Dropdowns First
  useEffect(() => {
    const loadInitialDropdowns = async () => {
      try {
        setLoading(true);
        await Promise.all([
          loadCorporates(),
          loadPackages(),
          loadProviders(),
        ]);
        setDropdownsLoaded(true);
      } catch (err) {
        Swal.fire("Error", "Failed to load dropdowns", "error");
      } finally {
        setLoading(false);
      }
    };

    loadInitialDropdowns();
  }, []);

  // Load Setup Details AFTER Dropdowns are Loaded
  useEffect(() => {
    if (setupId && dropdownsLoaded && providersLoaded) {
      loadSetupDetails();
    }
  }, [dropdownsLoaded, providersLoaded, setupId]);

  const loadCorporates = async () => {
    try {
      const res = await axios.get(`${BASE_URL}/ape-scheduler/corporates`, {
        headers: getAuthHeaders(),
      });
      setCorporates(getResponseArray(res.data));
    } catch (err) {
      Swal.fire("Error", "Failed to load corporates", "error");
      throw err; // To fail the Promise.all
    }
  };

  const loadPackages = async () => {
    try {
      const res = await axios.get(`${BASE_URL}/ape-scheduler/packages`, {
        headers: getAuthHeaders(),
      });
      setPackages(getResponseArray(res.data));
    } catch (err) {
      Swal.fire("Error", "Failed to load packages", "error");
      throw err;
    }
  };

  const loadProviders = async () => {
    try {
      const res = await axios.get(`${BASE_URL}/ape-scheduler/providers`, {
        headers: getAuthHeaders(),
      });
      setProviders(getResponseArray(res.data));
      setProvidersLoaded(true); // Mark as loaded
    } catch (err) {
      Swal.fire("Error", "Failed to load providers", "error");
      throw err;
    }
  };

  // Load Setup Details for Edit
  const loadSetupDetails = async () => {
    try {
      setFormLoading(true);
      const res = await axios.get(
        `${BASE_URL}/ape-scheduler/setups/${setupId}`,
        {
          headers: getAuthHeaders(),
        }
      );
      const setupPayload = res.data || {};
      const setup =
        setupPayload.setup ||
        setupPayload.data?.setup ||
        (setupPayload.corp_code ? setupPayload : null) ||
        (setupPayload.data?.corp_code ? setupPayload.data : null);
      if (!setup) {
        throw new Error("Setup details not found");
      }

      setSetupData(setup);

      // Populate form with dropdowns now loaded
      const corpOption = corporates.find((c) => c.value === setup.corp_code) || {
        value: setup.corp_code,
        label: `[${setup.corp_code}] ${setup.corp_name || setup.corp_code}`,
      };
      if (corpOption) {
        setValue("corporate", corpOption);
        setSelectedCorp(corpOption);
      }

      // Set plan
      const planOption = {
        value: setup.plan_code,
        label: `[${setup.plan_code}] ${setup.plan_name || setup.plan_code}`,
      };
      setValue("plan", planOption);

      setValue("memberType", getMemberTypeLabel(setup.member_type));

      setValue("gender", setup.gender || "All"); // Add gender population

      setValue("minAge", setup.min_age || 1);
      setValue("maxAge", setup.max_age || 120);

      // Set package
      const packageOption = packages.find((p) => p.value === setup.ape_package) || {
        value: setup.ape_package,
        label: setup.package_name || setup.ape_package,
      };
      setValue("package", packageOption);

      // Set provider - now that providers are loaded, find matching option
      const providerOption = providers.find((p) => p.value === setup.provider_code) || {
        value: setup.provider_code,
        label: setup.provider_name || setup.provider_code,
      };
      setValue("provider", providerOption);

      setValue("remarks", setup.remarks || "");
      setValue("amount", Number(Number(setup.amount || 0).toFixed(2)) || 0);
      setStartDate(new Date(setup.date_from));
      setEndDate(new Date(setup.date_to));
      setValue("scheduleType", setup.schedule_type || "S1");

      // Load draft members and set as selected
      const memberOptions = getResponseArray(
        setupPayload.draft_members || setupPayload.data?.draft_members || setup.draft_members
      );
      setDraftMembers(memberOptions);
      draftValuesRef.current = memberOptions.map(m => m.value);
      setValue("members", memberOptions);
      setSelectAll(memberOptions.length > 0);

      // Initial load of members list including drafts
      loadMembersList(true, memberOptions); // Force include drafts
    } catch (err) {
      Swal.fire("Error", "Failed to load draft data", "error");
    } finally {
      setFormLoading(false);
    }
  };

  // Helper: Check if member is selectable (all critical flags true) - Aligned with Add
  const isMemberSelectable = (member) => {
    return (
      member.is_covered &&
      member.is_corporate_active &&
      member.is_corporate_within_period &&
      member.is_member_active &&
      member.is_within_age_limit &&
      member.is_plan_active &&
      member.has_provider_access &&
      member.is_coverage_active &&
      member.has_no_pending_admission &&
      member.is_maternity_eligible &&
      !member.is_ape_used &&
      member.is_provider_type_allowed &&
      member.is_provider_accredited &&
      !member.is_scheduled
    );
  };

  // Helper: Get reasons why not selectable (for badges/tooltips) - Aligned with Add
  const getNonSelectableReasons = (member) => {
    const reasons = [];
    if (!member.is_covered) reasons.push("APE not covered");
    if (!member.is_corporate_active) reasons.push("Corporate inactive");
    if (!member.is_corporate_within_period) reasons.push("Corporate expired");
    if (!member.is_member_active) reasons.push("Member inactive");
    if (!member.is_within_age_limit) reasons.push("Age limit exceeded");
    if (!member.is_plan_active) reasons.push("Plan inactive");
    if (!member.has_provider_access) reasons.push("No provider access");
    if (!member.is_coverage_active) reasons.push("Coverage inactive");
    if (!member.has_no_pending_admission) reasons.push("Pending admission");
    if (!member.is_maternity_eligible) reasons.push("Not maternity eligible");
    if (member.is_ape_used) reasons.push("APE already used");
    if (!member.is_provider_type_allowed) reasons.push("Provider type not allowed");
    if (!member.is_provider_accredited) reasons.push("Provider not accredited");
    if (member.is_scheduled) reasons.push("Already scheduled");
    return reasons;
  };

  const filteredMembersList = useMemo(() => {
    const keywords = memberSearch.trim().toLowerCase().split(/\s+/).filter(Boolean);
    if (keywords.length === 0) return membersList;

    return membersList.filter((member) => {
      const searchableText = [
        member.member_id,
        member.value,
        member.first_name,
        member.last_name,
        member.fullname,
        member.label,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return keywords.every((keyword) => searchableText.includes(keyword));
    });
  }, [membersList, memberSearch]);

  // Function to load members list, merging with drafts - Aligned with Add (add date/provider params)
  const loadMembersList = useCallback(async (includeDrafts = false, draftOverride = null) => {
    if (!corporate?.value || isResetting) return;
    setFormLoading(true);
    // Reset selections when list refreshes (but preserve drafts)
    setSelectAll(false);
    const currentSelected = watch("members") || [];
    const draftsToMerge = Array.isArray(draftOverride) ? draftOverride : draftMembers;
    const params = new URLSearchParams({
      plan_code: plan?.value || setupData?.plan_code || "All",
      member_type: memberType,
      gender: gender,
      min_age: minAge || 0,
      max_age: maxAge || 150,
      coverage_code: "APE", // Always append for backend flags
    });
    // Append scheduled check params only if provider and dates are selected - Aligned with Add
    if (provider?.value && startDate && endDate) {
      const dateFrom = startDate.toISOString().split("T")[0];
      const dateTo = endDate.toISOString().split("T")[0];
      params.append("provider_code", provider.value);
      params.append("date_from", dateFrom);
      params.append("date_to", dateTo);
    }
    try {
      const res = await axios.get(`${BASE_URL}/ape-scheduler/members/${corporate.value}?${params}`, {
        headers: getAuthHeaders(),
      });
      let fullList = getResponseArray(res.data);
      // Merge with draft members to ensure they are shown even if not in filtered list
      if (includeDrafts) {
        const additionalDrafts = draftsToMerge.filter(dm => !fullList.some(ml => ml.value === dm.value));
        fullList = [...fullList, ...additionalDrafts];
      }
      setMembersList(fullList);
      // Update selected to include any new drafts (avoid dep loop)
      const selectedList = Array.isArray(currentSelected) ? currentSelected : [];
      const updatedSelected = [...selectedList, ...draftsToMerge.filter(ad => !selectedList.some(sm => sm.value === ad.value))];
      setValue("members", updatedSelected);
    } catch (err) {
      console.error("Error loading members:", err);
    } finally {
      setFormLoading(false);
    }
  }, [corporate, plan, memberType, gender, minAge, maxAge, provider, startDate, endDate, setupData, draftMembers, isResetting, setValue, watch]);

  // Cascade Plans (Trigger after corporate is set) - Fixed deps
  useEffect(() => {
    if (corporate?.value && setupData) {
      const loadKey = `${corporate.value}:${setupData.plan_code || ""}`;
      if (plansLoadedKeyRef.current === loadKey) return;
      plansLoadedKeyRef.current = loadKey;

      setIsResetting(true);
      setFormLoading(true);
      axios
        .get(`${BASE_URL}/ape-scheduler/plans/${corporate.value}`, {
          headers: getAuthHeaders(),
        })
        .then((res) => {
          const planOptions = getResponseArray(res.data);
          setPlans(planOptions.length ? planOptions : [{ value: "All", label: "All" }]);
          const existingPlan = planOptions.find(
            (p) => p.value === setupData.plan_code
          );
          if (existingPlan) {
            setValue("plan", existingPlan);
          }
        })
        .catch((err) => {
          console.error("Error loading plans:", err);
          setPlans([{ value: "All", label: "All" }]);
        })
        .finally(() => {
          setFormLoading(false);
          setIsResetting(false);
        });
    }
  }, [corporate?.value, setupData?.plan_code, setValue]);

  // Cascade Members List - Fixed deps, add guard
  useEffect(() => {
    if (!formLoading) { // Guard to debounce
      loadMembersList(true); // Always include drafts
    }
  }, [
    corporate,
    plan,
    memberType,
    gender,
    minAge,
    maxAge,
    provider,
    startDate,
    endDate,
    loadMembersList, // useCallback dep
  ]);

  // Sync Select All state with selections (now considering only selectable members based on all flags) - Aligned with Add
  useEffect(() => {
    if (membersList.length > 0) {
      const availableMembers = membersList.filter((m) => isMemberSelectable(m));
      const allAvailableSelected =
        availableMembers.length > 0 &&
        availableMembers.every((m) =>
          selectedMembers.some((s) => s.value === m.value)
        );
      setSelectAll(allAvailableSelected);
    }
  }, [selectedMembers, membersList]);

  // Select All Handler (now only selects available members) - Aligned with Add
  const handleSelectAllChange = useCallback((e) => {
    const checked = e.target.checked;
    setSelectAll(checked);
    if (checked) {
      const available = membersList.filter(isMemberSelectable);
      setValue("members", available);
    } else {
      setValue("members", []);
    }
  }, [membersList, setValue]);

  // Individual member toggle - Ensure it works with drafts, only if selectable
  const handleMemberToggle = useCallback((member, checked) => {
    if (!isMemberSelectable(member)) return; // Align with Add: only toggle selectables
    const current = watch("members");
    if (checked) {
      if (!current.some(m => m.value === member.value)) {
        setValue("members", [...current, member]);
      }
    } else {
      setValue("members", current.filter((m) => m.value !== member.value));
    }
  }, [setValue, watch]);

  function loadSchedules(page = 1, searchValue = tableSearch) {
    if (!setupId) return;

    setLoading(true);
    const params = new URLSearchParams({
      per_page: 10,
      page,
    });
    const trimmedSearch = searchValue.trim();
    if (trimmedSearch) {
      params.append("search", trimmedSearch);
    }

    axios
      .get(`${BASE_URL}/ape-scheduler/setups/${setupId}/schedules?${params}`, {
        headers: getAuthHeaders(),
      })
      .then((res) => {
        const schedules = getResponseArray(res.data);
        setTableData({
          data: schedules,
          pagination: normalizePagination(res.data.pagination, schedules, page, 10),
        });
      })
      .catch((err) => console.error("Error loading schedules:", err))
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    if (!setupData) return;

    const timer = setTimeout(() => {
      loadSchedules(1, tableSearch);
    }, 250);

    return () => clearTimeout(timer);
  }, [setupData?.status_flag, setupId, tableSearch]);

  const onSubmitUpdateDraft = (data) => {
    setFormLoading(true);
    const submitData = {
      ...data,
      corp_code: corporate?.value,
      date_from: startDate.toISOString().split("T")[0],
      date_to: endDate.toISOString().split("T")[0],
      member_type: memberType,
      members: data.members.map((m) => m.value), // Extract values
      setup_id: setupId, // Include for update
    };
    axios
      .put(`${BASE_URL}/ape-scheduler/setups/${setupId}`, submitData, {
        headers: getAuthHeaders(),
      })
      .then(async () => {
        Swal.fire("Success", "Draft updated!", "success");
        await loadSetupDetails();
        loadSchedules(1, tableSearch);
      })
      .catch((error) => {
        // Align error handling with Add
        let errorMessage = "An unexpected error occurred";
        if (error.response) {
          const status = error.response.status;
          const errData = error.response.data;
          if (status === 422) {
            errorMessage = errData.message ? (typeof errData.message === 'object' ? Object.values(errData.message)[0][0] : errData.message) : "Validation failed";
          } else if (status === 409) {
            errorMessage = errData.message || "Duplicate entry detected";
          } else if (status === 401) {
            errorMessage = "Unauthorized access";
          } else if (status === 404) {
            errorMessage = "Resource not found";
          } else if (status === 500) {
            errorMessage = "Server error";
          } else {
            errorMessage = errData.message || `Error ${status}`;
          }
        } else if (error.request) {
          errorMessage = "Network error. Please check your connection.";
        } else {
          errorMessage = error.message;
        }
        Swal.fire("Error!", errorMessage, "error");
      })
      .finally(() => setFormLoading(false));
  };

  const onSubmitFinalize = (data) => {
    Swal.fire({
      title: "Finalize Updated Schedule?",
      text: "This will create final entries and clear draft.",
      icon: "warning",
      showCancelButton: true,
    }).then((result) => {
      if (result.isConfirmed) {
        setFormLoading(true);
        const submitData = {
          ...data,
          date_from: startDate.toISOString().split("T")[0],
          date_to: endDate.toISOString().split("T")[0],
          members: data.members.map((m) => m.value),
          member_type: memberType,
          status_flag: "final",
          setup_id: setupId,
        };
        axios
          .post(`${BASE_URL}/ape-scheduler/schedules/finalize`, submitData, {
            headers: getAuthHeaders(),
          })
          .then(() => {
            Swal.fire("Success", "Schedule finalized!", "success");
            reset();
          })
          .catch((error) => {
            // Align error handling
            let errorMessage = "An unexpected error occurred";
            if (error.response) {
              const status = error.response.status;
              const errData = error.response.data;
              if (status === 422) {
                errorMessage = errData.message ? (typeof errData.message === 'object' ? Object.values(errData.message)[0][0] : errData.message) : "Validation failed";
              } else if (status === 409) {
                errorMessage = errData.message || "Duplicate entry detected";
              } else if (status === 401) {
                errorMessage = "Unauthorized access";
              } else if (status === 404) {
                errorMessage = "Resource not found";
              } else if (status === 500) {
                errorMessage = "Server error";
              } else {
                errorMessage = errData.message || `Error ${status}`;
              }
            } else if (error.request) {
              errorMessage = "Network error. Please check your connection.";
            } else {
              errorMessage = error.message;
            }
            Swal.fire("Error!", errorMessage, "error");
          })
          .finally(() => setFormLoading(false));
      }
    });
  };

  const onSubmitSetup = async (data) => {
    const confirmResult = await Swal.fire({
      title: "Set-up Updated Schedule?",
      text: "This will check for duplicates and update final entries.",
      icon: "warning",
      showCancelButton: true,
    });

    if (confirmResult.isConfirmed) {
      const loadingSwal = Swal.fire({
        title: "Please wait...",
        text: "While setting up your updated APE Schedule",
        allowOutsideClick: false,
        showConfirmButton: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      try {
        const submitData = {
          ...data,
          corp_code: corporate?.value,
          member_type: memberType,
          members: data.members.map((m) => m.value),
          coverage_code: "APE", // Default
          client_code: selectedCorp?.label.split("[")[1]?.split("]")[0] || null, // Extract from label if needed
          gender: data?.gender || null,
          min_age: data?.minAge || 0,
          max_age: data?.maxAge || 150,
          ape_package: data?.package?.value || null,
          remarks: data?.remarks || "",
          amount: data?.amount || 0,
          plan_code: data?.plan?.value || "All",
          provider_code: data?.provider?.value || "All",
          start_date: startDate.toISOString().split("T")[0],
          end_date: endDate.toISOString().split("T")[0],
          date_from: startDate.toISOString().split("T")[0],
          date_to: endDate.toISOString().split("T")[0],
          package_code: data?.package?.value || null,
          schedule_type: data?.scheduleType || null,
          setup_id: setupId, // For update
        };
        const response = await axios.post(`${BASE_URL}/ape-scheduler/setups?action=final`, submitData, {
          headers: getAuthHeaders(),
        });

        Swal.close(); // Close loading modal
        if (response.data.status === 'success') {
          Swal.fire({
            title: "Success!",
            text: response.data.message || "Schedule updated and set up successfully!",
            icon: "success",
            confirmButtonText: "OK",
          });
          reset();
        } else {
          throw new Error(response.data.message || "Unknown error occurred");
        }
      } catch (error) {
        Swal.close(); // Close loading modal
        let errorMessage = "An unexpected error occurred";
        if (error.response) {
          // Server responded with error status
          const status = error.response.status;
          const data = error.response.data;
          if (status === 422) {
            // Validation errors
            errorMessage = data.message ? (typeof data.message === 'object' ? Object.values(data.message)[0][0] : data.message) : "Validation failed";
          } else if (status === 409) {
            errorMessage = data.message || "Duplicate entry detected";
          } else if (status === 401) {
            errorMessage = "Unauthorized access";
          } else if (status === 404) {
            errorMessage = "Resource not found";
          } else if (status === 500) {
            errorMessage = "Server error";
          } else {
            errorMessage = data.message || `Error ${status}`;
          }
        } else if (error.request) {
          // Network error
          errorMessage = "Network error. Please check your connection.";
        } else {
          errorMessage = error.message;
        }
        Swal.fire({
          title: "Error!",
          text: errorMessage,
          icon: "error",
          confirmButtonText: "OK",
        });
      }
    }
  };

  const handleDelete = (tblId, memberName, memberId) => {
    Swal.fire({
      title: "Remove Member?",
      text: `Remove ${memberName || "this member"} from this schedule?`,
      icon: "warning",
      showCancelButton: true,
    }).then((result) => {
      if (result.isConfirmed) {
        axios
          .delete(`${BASE_URL}/ape-scheduler/schedules/${tblId}`, {
            headers: getAuthHeaders(),
          })
          .then(async () => {
            Swal.fire("Removed!", "Member removed from schedule.", "success");
            if (memberId) {
              const removedMemberId = String(memberId);
              const remainingSelected = (watch("members") || []).filter(
                (member) => String(member.value) !== removedMemberId
              );
              setValue("members", remainingSelected);
              setDraftMembers((currentDrafts) =>
                currentDrafts.filter((member) => String(member.value) !== removedMemberId)
              );
              draftValuesRef.current = draftValuesRef.current.filter(
                (value) => String(value) !== removedMemberId
              );
            }
            await loadSetupDetails();
            loadSchedules(schedulePagination.current_page, tableSearch);
          })
          .catch((err) => Swal.fire("Error", "Failed to delete schedule", "error"));
      }
    });
  };

  const handleSendEmail = (recipient, corpCode) => {
    const text =
      recipient === "corporate"
        ? "to Corporate"
        : recipient === "provider"
        ? "to Provider"
        : "to Both";
    Swal.fire({
      title: `Send Email ${text}?`,
      icon: "info",
      showCancelButton: true,
    }).then((result) => {
      if (result.isConfirmed) {
        axios
          .post(
            `${BASE_URL}/ape-scheduler/send-email/${recipient}`,
            { corp_code: corpCode },
            { headers: getAuthHeaders() }
          )
          .then(() =>
            Swal.fire("Sent!", "Email sent successfully.", "success")
          )
          .catch((err) => Swal.fire("Error", "Failed to send email", "error"));
      }
    });
  };

  const handlePrint = () => {
    // Use window.print() or client-side PDF lib; or call API
    window.print();
  };

  if (loading) {
    return <div className="p-6">Loading dropdowns...</div>;
  }

  if (formLoading && !setupData) {
    return <div className="p-6">Loading draft...</div>;
  }

  const schedulePagination =
    tableData.pagination || normalizePagination(null, tableData.data || []);
  const scheduleColumns = [
    { key: "trans_no", label: "TRN#" },
    { key: "loa_no", label: "LOA#" },
    { key: "corp_name", label: "Corporate", render: (schedule) => schedule.corp_name || schedule.corp_code },
    { key: "package_name", label: "Package", render: (schedule) => schedule.package_name || schedule.package_code },
    { key: "provider_name", label: "Provider", render: (schedule) => schedule.provider_name || schedule.provider_code },
    { key: "coverage_code", label: "Coverage" },
    { key: "plan_name", label: "Plan", render: (schedule) => schedule.plan_name || schedule.plan_code },
    { key: "member_type", label: "Member Type" },
    { key: "member_name", label: "Member Name", render: (schedule) => schedule.member_name || schedule.member_id },
    { key: "amount", label: "Amount", render: (schedule) => formatAmount(schedule.amount), align: "right" },
    { key: "start_date", label: "Date from", render: (schedule) => formatDateOnly(schedule.start_date) },
    { key: "end_date", label: "Date to", render: (schedule) => formatDateOnly(schedule.end_date) },
    { key: "remarks", label: "Remarks" },
    { key: "status_flag", label: "Status" },
  ];

  return (
    <div className="p-6 max-w-7xl bg-gray-50 min-h-screen">
      <style>{APE_DATEPICKER_PORTAL_STYLE}</style>
      {/* Form Section */}
      <form
        onSubmit={handleSubmit(onSubmitUpdateDraft)}
        className="bg-white p-6 rounded-xl shadow-lg mb-6 transition-shadow duration-300 hover:shadow-xl"
      >
        <h2 className="text-2xl font-bold mb-6 text-gray-800 border-b pb-2">
          ✏️ Edit APE Schedule Draft (ID: {setupId})
        </h2>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left Panel: Fields */}
          <div className="space-y-4">
            <div className="relative">
              <label className="block text-sm font-semibold mb-2 text-gray-700">
                Select Account *
              </label>
              <Controller
                name="corporate"
                control={control}
                rules={{ required: "Required" }}
                render={({ field }) => (
                  <Select
                    {...field}
                    options={corporates}
                    onChange={(opt) => {
                      field.onChange(opt);
                      setSelectedCorp(opt);
                    }}
                    isLoading={formLoading}
                    classNamePrefix="react-select"
                    className="basic-select"
                    placeholder="Select Corporate"
                    isDisabled={!!setupData} // Disable if editing fixed corp
                    styles={{
                      control: (provided) => ({
                        ...provided,
                        borderColor: errors.corporate ? "#ef4444" : "#d1d5db",
                        "&:hover": {
                          borderColor: errors.corporate ? "#ef4444" : "#9ca3af",
                        },
                      }),
                    }}
                  />
                )}
              />
              {errors.corporate && (
                <p className="text-red-500 text-xs mt-1 flex items-center">
                  <FontAwesomeIcon icon={faExclamationCircle} className="mr-1" />
                  {errors.corporate.message}
                </p>
              )}
            </div>
            <div className="relative">
              <label className="block text-sm font-semibold mb-2 text-gray-700">
                Select Plan *
              </label>
              <Controller
                name="plan"
                control={control}
                rules={{ required: "Required" }}
                render={({ field }) => (
                  <Select
                    {...field}
                    options={plans}
                    isLoading={formLoading}
                    classNamePrefix="react-select"
                    className="basic-select"
                    placeholder="Select Plan"
                    styles={{
                      control: (provided) => ({
                        ...provided,
                        borderColor: errors.plan ? "#ef4444" : "#d1d5db",
                        "&:hover": {
                          borderColor: errors.plan ? "#ef4444" : "#9ca3af",
                        },
                      }),
                    }}
                  />
                )}
              />
              {errors.plan && (
                <p className="text-red-500 text-xs mt-1 flex items-center">
                  <FontAwesomeIcon icon={faExclamationCircle} className="mr-1" />
                  {errors.plan.message}
                </p>
              )}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold mb-2 text-gray-700">
                  Member Type *
                </label>
                <select
                  {...register("memberType", { required: true })}
                  className={`form-select w-full p-3 border rounded-lg transition-colors duration-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    errors.memberType
                      ? "border-red-300 focus:ring-red-500"
                      : "border-gray-300 focus:ring-blue-500"
                  }`}
                >
                  <option>All</option>
                  <option>Principal</option>
                  <option>Dependent</option>
                </select>
                {errors.memberType && (
                  <p className="text-red-500 text-xs mt-1 flex items-center">
                    <FontAwesomeIcon icon={faExclamationCircle} className="mr-1" />
                    Required
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm font-semibold mb-2 text-gray-700">
                  Gender *
                </label>
                <select
                  {...register("gender", { required: true })}
                  className={`form-select w-full p-3 border rounded-lg transition-colors duration-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    errors.gender
                      ? "border-red-300 focus:ring-red-500"
                      : "border-gray-300 focus:ring-blue-500"
                  }`}
                >
                  <option>All</option>
                  <option>Male</option>
                  <option>Female</option>
                </select>
                {errors.gender && (
                  <p className="text-red-500 text-xs mt-1 flex items-center">
                    <FontAwesomeIcon icon={faExclamationCircle} className="mr-1" />
                    Required
                  </p>
                )}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold mb-2 text-gray-700">
                  Min Age *
                </label>
                <input
                  type="number"
                  {...register("minAge", { required: true, min: 0 })}
                  className={`form-input w-full p-3 border rounded-lg transition-colors duration-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    errors.minAge
                      ? "border-red-300 focus:ring-red-500"
                      : "border-gray-300 focus:ring-blue-500"
                  }`}
                />
                {errors.minAge && (
                  <p className="text-red-500 text-xs mt-1 flex items-center">
                    <FontAwesomeIcon icon={faExclamationCircle} className="mr-1" />
                    Required (min 0)
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm font-semibold mb-2 text-gray-700">
                  Max Age *
                </label>
                <input
                  type="number"
                  {...register("maxAge", { required: true, min: 0 })}
                  className={`form-input w-full p-3 border rounded-lg transition-colors duration-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    errors.maxAge
                      ? "border-red-300 focus:ring-red-500"
                      : "border-gray-300 focus:ring-blue-500"
                  }`}
                />
                {errors.maxAge && (
                  <p className="text-red-500 text-xs mt-1 flex items-center">
                    <FontAwesomeIcon icon={faExclamationCircle} className="mr-1" />
                    Required (min 0)
                  </p>
                )}
              </div>
            </div>
            <div>
              <label className="block text-sm font-semibold mb-2 text-gray-700">
                Schedule Type *
              </label>
              <select
                {...register("scheduleType", { required: true })}
                className="form-select w-full p-3 border rounded-lg transition-colors duration-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent border-gray-300"
              >
                <option value="S1">Single Day</option>
                <option value="S2">Two Days</option>
                {/* Add more from old JS if needed */}
              </select>
            </div>
            <div className="relative">
              <label className="block text-sm font-semibold mb-2 text-gray-700">
                Select APE Package *
              </label>
              <Controller
                name="package"
                control={control}
                rules={{ required: "Required" }}
                render={({ field }) => (
                  <Select
                    {...field}
                    options={packages}
                    isLoading={formLoading}
                    classNamePrefix="react-select"
                    className="basic-select"
                    placeholder="Select Package"
                    styles={{
                      control: (provided) => ({
                        ...provided,
                        borderColor: errors.package ? "#ef4444" : "#d1d5db",
                        "&:hover": {
                          borderColor: errors.package ? "#ef4444" : "#9ca3af",
                        },
                      }),
                    }}
                  />
                )}
              />
              {errors.package && (
                <p className="text-red-500 text-xs mt-1 flex items-center">
                  <FontAwesomeIcon icon={faExclamationCircle} className="mr-1" />
                  {errors.package.message}
                </p>
              )}
            </div>
            <div className="relative">
              <label className="block text-sm font-semibold mb-2 text-gray-700">
                Search Provider *
              </label>
              <Controller
                name="provider"
                control={control}
                rules={{ required: "Required" }}
                render={({ field }) => (
                  <Select
                    {...field}
                    options={providers}
                    isLoading={formLoading}
                    classNamePrefix="react-select"
                    className="basic-select"
                    placeholder="Select Provider"
                    styles={{
                      control: (provided) => ({
                        ...provided,
                        borderColor: errors.provider ? "#ef4444" : "#d1d5db",
                        "&:hover": {
                          borderColor: errors.provider ? "#ef4444" : "#9ca3af",
                        },
                      }),
                    }}
                  />
                )}
              />
              {errors.provider && (
                <p className="text-red-500 text-xs mt-1 flex items-center">
                  <FontAwesomeIcon icon={faExclamationCircle} className="mr-1" />
                  {errors.provider.message}
                </p>
              )}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold mb-2 text-gray-700">
                  Date From *
                </label>
                <DatePicker
                  selected={startDate}
                  onChange={setStartDate}
                  className="form-input w-full p-3 border rounded-lg transition-colors duration-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent border-gray-300"
                  wrapperClassName="w-full"
                  portalId={APE_DATEPICKER_PORTAL_ID}
                  popperClassName={APE_DATEPICKER_POPPER_CLASS}
                  popperPlacement="bottom-start"
                  popperProps={{ strategy: "fixed" }}
                  dateFormat="yyyy-MM-dd"
                  placeholderText="Select date"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold mb-2 text-gray-700">
                  Date To *
                </label>
                <DatePicker
                  selected={endDate}
                  onChange={setEndDate}
                  className="form-input w-full p-3 border rounded-lg transition-colors duration-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent border-gray-300"
                  wrapperClassName="w-full"
                  portalId={APE_DATEPICKER_PORTAL_ID}
                  popperClassName={APE_DATEPICKER_POPPER_CLASS}
                  popperPlacement="bottom-start"
                  popperProps={{ strategy: "fixed" }}
                  dateFormat="yyyy-MM-dd"
                  placeholderText="Select date"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-semibold mb-2 text-gray-700">
                Remarks
              </label>
              <textarea
                {...register("remarks")}
                className="form-textarea w-full p-3 border rounded-lg transition-colors duration-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent border-gray-300"
                rows={3}
                placeholder="Enter remarks..."
              />
            </div>
            <div>
              <label className="block text-sm font-semibold mb-2 text-gray-700">
                Amount *
              </label>
              <input
                type="number"
                step="0.01"
                {...register("amount", { required: true, min: 0 })}
                className={`form-input w-full p-3 border rounded-lg transition-colors duration-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  errors.amount
                    ? "border-red-300 focus:ring-red-500"
                    : "border-gray-300 focus:ring-blue-500"
                }`}
                placeholder="0.00"
              />
              {errors.amount && (
                <p className="text-red-500 text-xs mt-1 flex items-center">
                  <FontAwesomeIcon icon={faExclamationCircle} className="mr-1" />
                  Required (min 0)
                </p>
              )}
            </div>
          </div>
          {/* Right Panel: Scrollable Member Checkboxes - Aligned with Add (badges, disabled) */}
          <div className="space-y-4">
            <label className="block text-sm font-semibold mb-2 text-gray-700">
              Select Members * (Loaded from Draft: {draftMembers.length} pre-selected)
            </label>
            <div className="flex justify-between items-center mb-3 bg-gray-100 p-2 rounded-lg">
              <label className="flex items-center cursor-pointer text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors duration-200">
                <input
                  type="checkbox"
                  checked={selectAll}
                  onChange={handleSelectAllChange}
                  className="mr-2 w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                />
                <FontAwesomeIcon
                  icon={selectAll ? faCheckSquare : faSquare}
                  className="mr-2 text-gray-500"
                />
                {selectAll ? "Deselect All" : "Select All"}
              </label>
              <span className="text-sm font-semibold text-blue-600 bg-blue-100 px-2 py-1 rounded-full">
                {selectedMembers.length} selected
              </span>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-3 shadow-sm">
              <label
                htmlFor="edit-member-search"
                className="block text-xs font-semibold text-gray-600 mb-2"
              >
                Search members
              </label>
              <div className="flex flex-col sm:flex-row gap-2">
                <div className="relative flex-1">
                  <FontAwesomeIcon
                    icon={faSearch}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"
                  />
                  <input
                    id="edit-member-search"
                    type="text"
                    value={memberSearch}
                    onChange={(e) => setMemberSearch(e.target.value)}
                    className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm transition-colors duration-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Search by first name, last name, or member ID"
                    disabled={formLoading || membersList.length === 0}
                  />
                </div>
                {memberSearch && (
                  <button
                    type="button"
                    onClick={() => setMemberSearch("")}
                    className="px-3 py-2 text-sm font-medium text-gray-600 bg-gray-100 border border-gray-200 rounded-lg hover:bg-gray-200 transition-colors duration-200"
                  >
                    Clear
                  </button>
                )}
              </div>
              <p className="mt-2 text-xs text-gray-500">
                Showing {filteredMembersList.length} of {membersList.length} members
              </p>
            </div>
            <div className="border-2 border-gray-200 rounded-lg h-[30rem] overflow-y-auto p-3 bg-white shadow-inner">
              {formLoading ? (
                <div className="flex justify-center items-center h-full">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  <span className="ml-2 text-gray-500">Loading members...</span>
                </div>
              ) : membersList.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <FontAwesomeIcon icon={faSquare} className="text-4xl mb-2" />
                  <p>No members found. Adjust filters above.</p>
                </div>
              ) : filteredMembersList.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <FontAwesomeIcon icon={faSearch} className="text-4xl mb-2" />
                  <p>No matching members found.</p>
                  <p className="text-xs mt-1">
                    Try a different first name, last name, or member ID.
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  {filteredMembersList.map((member, index) => {
                    const isSelected = selectedMembers.some(
                      (m) => m.value === member.value
                    );
                    const isSelectable = isMemberSelectable(member);
                    const reasons = getNonSelectableReasons(member);
                    const tooltipText = reasons.length > 0 ? reasons.join(", ") : "";
                    return (
                      <label
                        key={member.value}
                        className={`flex items-center p-3 rounded-lg cursor-pointer transition-all duration-200 hover:bg-blue-50 flex-wrap ${
                          isSelected
                            ? "bg-blue-50 border-2 border-blue-200"
                            : !isSelectable
                            ? "opacity-60 cursor-not-allowed bg-gray-100"
                            : ""
                        }`}
                        title={tooltipText} // Hover tooltip for all reasons
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          disabled={!isSelectable}
                          onChange={(e) => handleMemberToggle(member, e.target.checked)}
                          className="mr-3 w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2 disabled:opacity-50"
                        />
                        <span className={`text-sm font-medium flex-1 ${!isSelectable ? 'text-gray-400' : 'text-gray-700'}`}>
                          {member.label}
                        </span>
                        {/* Multiple Badges for Reasons - Aligned with Add */}
                        <div className="flex flex-wrap gap-1 ml-2">
                          {reasons.map((reason, rIndex) => (
                            <span
                              key={rIndex}
                              className="text-xs px-2 py-1 rounded-full bg-red-100 text-red-700 border border-red-200 flex items-center whitespace-nowrap"
                            >
                              <FontAwesomeIcon 
                                icon={
                                  reason.includes("Corporate") ? faBuilding :
                                  reason.includes("Member") ? faUser :
                                  reason.includes("Age") ? faChild :
                                  reason.includes("Plan") ? faCalendarAlt :
                                  reason.includes("Provider access") ? faStethoscope :
                                  reason.includes("Coverage") ? faShieldAlt :
                                  reason.includes("Pending") ? faClock :
                                  reason.includes("Maternity") ? faVenusMars :
                                  reason.includes("APE") ? faFileMedical :
                                  reason.includes("Type") ? faExclamationCircle :
                                  reason.includes("Accredited") ? faBan :
                                  reason.includes("Scheduled") ? faExclamationTriangle : faExclamationTriangle
                                } 
                                className="mr-1 text-xs" 
                              />
                              {reason.split(" ")[0]} {/* Shorten for space */}
                            </span>
                          ))}
                        </div>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
            {errors.members && (
              <p className="text-red-500 text-xs mt-1 flex items-center bg-red-50 p-2 rounded">
                <FontAwesomeIcon icon={faExclamationCircle} className="mr-1" />
                Select at least one member
              </p>
            )}
          </div>
        </div>
        <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-4 mt-6 p-4 border-t bg-gray-50 rounded-b-xl">
          <button
            type="button"
            onClick={handleSubmit(onSubmitSetup)}
            className="btn bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-semibold transition-all duration-200 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
            disabled={formLoading || selectedMembers.length === 0}
          >
            <FontAwesomeIcon icon={faCheck} className="mr-2" />
            Update & Set-up Schedule
          </button>
          <button
            type="submit"
            className="btn bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg font-semibold transition-all duration-200 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
            disabled={formLoading}
          >
            <FontAwesomeIcon icon={faSave} className="mr-2" />
            Update Draft
          </button>
          <button
            type="button"
            onClick={handleSubmit(onSubmitFinalize)}
            className="btn bg-yellow-600 hover:bg-yellow-700 text-white px-6 py-3 rounded-lg font-semibold transition-all duration-200 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
            disabled={formLoading || selectedMembers.length === 0}
          >
            <FontAwesomeIcon icon={faCheck} className="mr-2" />
            Finalize Changes
          </button>
        </div>
      </form>

      {/* Schedules Table Section - Aligned with Add (render now) */}
      {corporate?.value && (
        <div className="bg-white p-6 rounded-xl shadow-lg mb-6">
          <div className="flex flex-col gap-3 border-b pb-4 mb-4 sm:flex-row sm:items-center sm:justify-between">
            <h2 className="text-2xl font-bold text-gray-800">
              Existing Members in Schedule
            </h2>
            <div className="relative w-full sm:w-80">
              <FontAwesomeIcon
                icon={faSearch}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"
              />
              <input
                type="text"
                value={tableSearch}
                onChange={(e) => setTableSearch(e.target.value)}
                className="w-full rounded-lg border border-gray-300 py-2 pl-9 pr-9 text-sm transition-colors duration-200 focus:border-transparent focus:ring-2 focus:ring-blue-500"
                placeholder="Search table..."
              />
              {tableSearch && (
                <button
                  type="button"
                  onClick={() => setTableSearch("")}
                  className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
                  title="Clear table search"
                >
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              )}
            </div>
          </div>
          {loading ? (
            <div className="flex justify-center py-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div></div>
          ) : tableData.data.length === 0 ? (
            <p className="text-gray-500">No members found in this schedule.</p>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-[1800px] bg-white border border-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    {scheduleColumns.map((column) => (
                      <th
                        key={column.key}
                        className={`px-4 py-3 text-xs font-medium uppercase tracking-wider text-gray-500 ${
                          column.align === "right" ? "text-right" : "text-left"
                        }`}
                      >
                        {column.label}
                      </th>
                    ))}
                    <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {tableData.data.map((schedule) => (
                    <tr key={schedule.tbl_id}>
                      {scheduleColumns.map((column) => (
                        <td
                          key={column.key}
                          className={`px-4 py-3 text-sm text-gray-900 ${
                            column.align === "right" ? "text-right" : "text-left"
                          } ${
                            column.key === "remarks" ? "min-w-[220px]" : "whitespace-nowrap"
                          }`}
                        >
                          {formatCell(column.render ? column.render(schedule) : schedule[column.key])}
                        </td>
                      ))}
                      <td className="px-4 py-3 text-center text-sm font-medium">
                        <button
                          type="button"
                          onClick={() => handleDelete(schedule.tbl_id, schedule.member_name, schedule.member_id)}
                          className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-red-200 bg-red-50 text-red-600 transition-colors hover:bg-red-100 hover:text-red-800 focus:outline-none focus:ring-2 focus:ring-red-400 focus:ring-offset-1"
                          title="Remove member from schedule"
                        >
                          <FontAwesomeIcon icon={faTimes} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
              {/* Basic Pagination - Expand with react-table if needed */}
              <div className="mt-4 flex flex-col gap-3 text-sm text-gray-600 sm:flex-row sm:items-center sm:justify-between">
                <span>{`Showing ${schedulePagination.from} to ${schedulePagination.to} of ${schedulePagination.total} results`}</span>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => loadSchedules(schedulePagination.current_page - 1, tableSearch)}
                    disabled={schedulePagination.current_page === 1}
                    className="rounded-md border border-gray-300 bg-white px-3 py-2 font-medium text-gray-700 transition-colors hover:bg-gray-50 hover:text-gray-900 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-white disabled:hover:text-gray-700"
                  >
                    Previous
                  </button>
                  <span className="rounded-md bg-gray-100 px-3 py-2 font-medium text-gray-700">
                    Page {schedulePagination.current_page} of {schedulePagination.last_page}
                  </span>
                  <button
                    type="button"
                    onClick={() => loadSchedules(schedulePagination.current_page + 1, tableSearch)}
                    disabled={schedulePagination.current_page === schedulePagination.last_page}
                    className="rounded-md border border-gray-300 bg-white px-3 py-2 font-medium text-gray-700 transition-colors hover:bg-gray-50 hover:text-gray-900 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-white disabled:hover:text-gray-700"
                  >
                    Next
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default ApeSchedulerEditPage;
